import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, ArrowLeft, RefreshCw } from 'lucide-react';
import { PrimaryButton, SecondaryButton } from './buttons';
import { toast } from 'react-hot-toast';
import {
    upsertLossesDelaysBulk,
    BulkUpdatePayload,
    getLossesDelays,
    deleteLossesDelaysBulk,
    LossDelayRecord as ApiLossDelayRecord
} from '../utils/lossesApi';

interface LossDelayRecord {
    id: string;
    dbId?: number;
    fabrica: string;
    filial: string;
    data: string;
    tipo: 'Perda' | 'Atraso';
    novo_m3: number | null;
    acoes_delete?: boolean; // For managing publish/delete actions
    isNew?: boolean; // To distinguish new vs existing records
}

// Dados das fábricas e filiais conforme especificação
const FABRICAS = [
    'Taquari',
    'Uberaba',
    'Itapetininga'
];

const FILIAIS_POR_FABRICA: Record<string, string[]> = {
    'Taquari': ['Dariano', 'Fratelli'],
    'Uberaba': ['Fratelli', 'Ian', 'Slongo', 'João'],
    'Itapetininga': ['Fratelli', 'Ian', 'Bartira', 'Slongo', 'João']
};

// Helper function to create empty record
const createEmptyRecord = (): LossDelayRecord => ({
    id: Date.now().toString() + Math.random(),
    fabrica: '',
    filial: '',
    data: '',
    tipo: 'Perda',
    novo_m3: null,
    isNew: true
});

interface LossesDelaysScreenProps {
    onBack: () => void;
}

export const LossesDelaysScreen: React.FC<LossesDelaysScreenProps> = ({ onBack }) => {
    // New records for data entry (always start empty)
    const [newRecords, setNewRecords] = useState<LossDelayRecord[]>([createEmptyRecord()]);

    // Existing records from database for management
    const [existingRecords, setExistingRecords] = useState<LossDelayRecord[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set());

    // Load existing losses and delays on component mount
    useEffect(() => {
        loadExistingRecords();
    }, []);

    const loadExistingRecords = async () => {
        setIsLoading(true);
        try {
            const response = await getLossesDelays();

            if (response.success && response.data) {
                const records: LossDelayRecord[] = response.data.map((record: ApiLossDelayRecord) => ({
                    id: record.id?.toString() || Date.now().toString(),
                    dbId: record.id,
                    fabrica: record.fabrica,
                    filial: record.filial,
                    data: record.data,
                    tipo: record.perda_ou_atraso.toLowerCase().includes('perda') ? 'Perda' as const : 'Atraso' as const,
                    novo_m3: record.nova_producao_m3 || null,
                    acoes_delete: record.acoes_delete || false,
                    isNew: false
                }));

                setExistingRecords(records);
                console.log(`✅ ${records.length} registro(s) existente(s) carregado(s)`);
            } else {
                setExistingRecords([]);
            }
        } catch (error) {
            console.error('Error loading existing records:', error);
            toast.error('Erro ao carregar registros existentes');
            setExistingRecords([]);
        } finally {
            setIsLoading(false);
        }
    };

    const addNewRecord = () => {
        const newRecord: LossDelayRecord = createEmptyRecord();
        setNewRecords([...newRecords, newRecord]);
    };

    const removeNewRecord = (id: string) => {
        if (newRecords.length > 1) {
            setNewRecords(newRecords.filter(record => record.id !== id));
        }
    };

    const updateNewRecord = (id: string, field: keyof LossDelayRecord, value: any) => {
        setNewRecords(newRecords.map(record => {
            if (record.id === id) {
                const updated = { ...record, [field]: value };

                // Reset filial when fabrica changes
                if (field === 'fabrica') {
                    updated.filial = '';
                }

                // Reset novo_m3 when tipo changes to Atraso
                if (field === 'tipo' && value === 'Atraso') {
                    updated.novo_m3 = null;
                }

                return updated;
            }
            return record;
        }));
    };

    const toggleRecordSelection = (id: string) => {
        const newSelected = new Set(selectedRecords);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedRecords(newSelected);
    };



    const deleteSelectedRecords = async () => {
        const recordsToDelete = existingRecords.filter(r => selectedRecords.has(r.id) && r.dbId);

        if (recordsToDelete.length === 0) {
            toast.error('Nenhum registro selecionado para exclusão');
            return;
        }

        if (!confirm(`Confirma a exclusão de ${recordsToDelete.length} registro(s)?`)) {
            return;
        }

        try {
            const idsToDelete = recordsToDelete.map(r => r.dbId!);
            const response = await deleteLossesDelaysBulk(idsToDelete);

            if (response.success) {
                // Remove deleted records from state
                setExistingRecords(prev => prev.filter(r => !selectedRecords.has(r.id)));
                setSelectedRecords(new Set());
                toast.success(`${recordsToDelete.length} registro(s) excluído(s) com sucesso`);
            } else {
                throw new Error(response.error || 'Erro ao deletar registros');
            }
        } catch (error) {
            console.error('Error deleting records:', error);
            toast.error('Erro ao deletar registros selecionados');
        }
    };

    const refreshData = () => {
        loadExistingRecords();
    };

    const handleSave = async () => {
        // Filter new records that have data to save
        const recordsToSave = newRecords.filter(record =>
            record.fabrica && record.filial && record.data
        );

        if (recordsToSave.length === 0) {
            toast.error('Nenhum registro válido para salvar.');
            return;
        }

        // Validação
        const hasErrors = recordsToSave.some(record => {
            if (record.tipo === 'Perda' && (record.novo_m3 === null || record.novo_m3 < 0)) {
                return true;
            }
            return false;
        });

        if (hasErrors) {
            toast.error('Por favor, preencha todos os campos obrigatórios corretamente.');
            return;
        }

        try {
            const payload: BulkUpdatePayload[] = recordsToSave.map(record => ({
                fabrica: record.fabrica,
                filial: record.filial,
                data: record.data,
                perda_ou_atraso: record.tipo,
                nova_producao_m3: record.tipo === 'Perda' ? (record.novo_m3 || 0) : 0
            }));

            const response = await upsertLossesDelaysBulk(payload);

            if (response.success) {
                const { created_count = 0 } = response.data || {};

                toast.success(`${created_count} novo(s) registro(s) criado(s) com sucesso!`);

                // Clear the form
                setNewRecords([createEmptyRecord()]);

                // Reload existing records to show the new additions
                await loadExistingRecords();
            } else {
                throw new Error(response.error || 'Erro ao salvar dados');
            }

        } catch (error) {
            console.error('Erro ao salvar perdas/atrasos:', error);
            toast.error(error instanceof Error ? error.message : 'Erro ao salvar perdas/atrasos.');
        }
    };

    return (
        <div className="min-h-screen bg-dexco-gray-bg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <SecondaryButton
                                onClick={onBack}
                                leftIcon={<ArrowLeft className="w-4 h-4" />}
                            >
                                Voltar
                            </SecondaryButton>
                            <h1 className="text-3xl font-display font-semibold text-dexco-text-dark">
                                Gerenciar Perdas e Atrasos
                            </h1>
                        </div>
                        <div className="flex items-center gap-3">
                            <SecondaryButton
                                onClick={refreshData}
                                leftIcon={<RefreshCw className="w-4 h-4" />}
                                disabled={isLoading}
                            >
                                Atualizar
                            </SecondaryButton>
                            {selectedRecords.size > 0 && (
                                <SecondaryButton
                                    onClick={deleteSelectedRecords}
                                    leftIcon={<Trash2 className="w-4 h-4" />}
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                >
                                    Excluir ({selectedRecords.size})
                                </SecondaryButton>
                            )}
                        </div>
                    </div>
                    <p className="text-gray-600 font-body">
                        Registre perdas de produção e atrasos nas fábricas. Visualize e gerencie os registros existentes.
                    </p>
                </div>

                {/* New Records Section */}
                <div className="bg-white rounded-xl shadow-lg border border-dexco-border overflow-hidden mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Adicionar Novos Registros</h3>
                        <p className="text-sm text-gray-600 mt-1">Preencha os dados para criar novas perdas ou atrasos</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-blue-50 border-b border-dexco-border">
                                    <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                        Fábrica
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                        Filial
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                        Data
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                        Perda OU Atraso
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                        Nova produção (m³)
                                    </th>
                                    <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider w-16">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-dexco-border">
                                {newRecords.map((record) => (
                                    <tr key={record.id} className="hover:bg-gray-50">
                                        {/* Fábrica */}
                                        <td className="px-6 py-4">
                                            <select
                                                value={record.fabrica}
                                                onChange={(e) => updateNewRecord(record.id, 'fabrica', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:border-transparent text-sm"
                                            >
                                                <option value="">Selecione uma fábrica</option>
                                                {FABRICAS.map(fabrica => (
                                                    <option key={fabrica} value={fabrica}>
                                                        {fabrica}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-6 py-4">
                                            <select
                                                value={record.filial}
                                                onChange={(e) => updateNewRecord(record.id, 'filial', e.target.value)}
                                                disabled={!record.fabrica}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:border-transparent text-sm disabled:bg-gray-100 disabled:text-gray-500"
                                            >
                                                <option value="">Selecione uma filial</option>
                                                {record.fabrica && FILIAIS_POR_FABRICA[record.fabrica]?.map(filial => (
                                                    <option key={filial} value={filial}>
                                                        {filial}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-6 py-4">
                                            <input
                                                type="date"
                                                value={record.data}
                                                onChange={(e) => updateNewRecord(record.id, 'data', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:border-transparent text-sm"
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <select
                                                value={record.tipo}
                                                onChange={(e) => updateNewRecord(record.id, 'tipo', e.target.value as 'Perda' | 'Atraso')}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:border-transparent text-sm"
                                            >
                                                <option value="Perda">Perda</option>
                                                <option value="Atraso">Atraso</option>
                                            </select>
                                        </td>
                                        <td className="px-6 py-4">
                                            {record.tipo === 'Perda' ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={record.novo_m3 || ''}
                                                    onChange={(e) => updateNewRecord(record.id, 'novo_m3', parseFloat(e.target.value) || null)}
                                                    placeholder="0.00"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:border-transparent text-sm"
                                                />
                                            ) : (
                                                <input
                                                    type="text"
                                                    value="não aplicável"
                                                    readOnly
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 text-sm"
                                                />
                                            )}
                                        </td>

                                        {/* Ações */}
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => removeNewRecord(record.id)}
                                                disabled={newRecords.length === 1}
                                                className="w-8 h-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Remover linha"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Add Row Button */}
                    <div className="px-6 py-4 border-t border-dexco-border bg-gray-50">
                        <SecondaryButton
                            onClick={addNewRecord}
                            leftIcon={<Plus className="w-4 h-4" />}
                        >
                            Adicionar Linha
                        </SecondaryButton>
                    </div>
                </div>

                {/* Save Button */}
                <div className="mb-8 flex justify-end">
                    <PrimaryButton
                        onClick={handleSave}
                        leftIcon={<Save className="w-4 h-4" />}
                    >
                        Salvar Novos Registros
                    </PrimaryButton>
                </div>

                {/* Existing Records Management Section */}
                <div className="bg-white rounded-xl shadow-lg border border-dexco-border overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Gerenciar Registros Existentes</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                {existingRecords.length > 0
                                    ? `${existingRecords.length} registro(s) encontrado(s). Selecione registros para exclusão se necessário.`
                                    : 'Nenhum registro existente encontrado.'
                                }
                            </p>
                        </div>
                        {selectedRecords.size > 0 && (
                            <SecondaryButton
                                onClick={deleteSelectedRecords}
                                leftIcon={<Trash2 className="w-4 h-4" />}
                                className="text-red-600 border-red-300 hover:bg-red-50"
                            >
                                Excluir ({selectedRecords.size})
                            </SecondaryButton>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="px-6 py-12 text-center">
                            <div className="flex items-center justify-center space-x-2">
                                <div className="w-4 h-4 border-2 border-dexco-green-primary border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-gray-600">Carregando registros existentes...</span>
                            </div>
                        </div>
                    ) : existingRecords.length === 0 ? (
                        <div className="px-6 py-12 text-center text-gray-500">
                            <p>Nenhum registro existente. Crie novos registros acima.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-dexco-border">
                                        <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider w-16">
                                            <input
                                                type="checkbox"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedRecords(new Set(existingRecords.map(r => r.id)));
                                                    } else {
                                                        setSelectedRecords(new Set());
                                                    }
                                                }}
                                                checked={selectedRecords.size > 0 && existingRecords.every(r => selectedRecords.has(r.id))}
                                                className="rounded border-gray-300 focus:ring-2 focus:ring-dexco-green-primary"
                                            />
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                            Fábrica
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                            Filial
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                            Data
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                            Tipo
                                        </th>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-dexco-text-dark uppercase tracking-wider">
                                            Nova produção (m³)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-dexco-border">
                                    {existingRecords.map((record) => (
                                        <tr key={record.id} className="hover:bg-gray-50">
                                            {/* Checkbox */}
                                            <td className="px-6 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRecords.has(record.id)}
                                                    onChange={() => toggleRecordSelection(record.id)}
                                                    className="rounded border-gray-300 focus:ring-2 focus:ring-dexco-green-primary"
                                                />
                                            </td>

                                            {/* Status */}
                                            <td className="px-6 py-4">
                                                {record.acoes_delete ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                        Oculto
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Publicado
                                                    </span>
                                                )}
                                            </td>

                                            {/* Fábrica */}
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {record.fabrica}
                                            </td>

                                            {/* Filial */}
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {record.filial}
                                            </td>

                                            {/* Data */}
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {new Date(record.data).toLocaleDateString('pt-BR')}
                                            </td>

                                            {/* Tipo */}
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {record.tipo}
                                            </td>

                                            {/* Nova produção */}
                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                {record.tipo === 'Perda' ? `${record.novo_m3} m³` : 'N/A'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}; 